import React, { useEffect, useState } from 'react';
import { getAllServices } from '../services/alerts.service';
import SortableTable from '../components/Alerts/SortableTable';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';
import { serviceStatusAllowsVendorTracking } from '../utils/vendorTracking';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [escalatedOnly, setEscalatedOnly] = useState(false);
  const navigate = useNavigate();

  const openService = (s, { track = false } = {}) => {
    const params = new URLSearchParams({
      case_id: String(s.case_id),
      service_id: String(s.id),
    });
    if (track) params.set('track', '1');
    navigate(`/add-service?${params.toString()}`);
  };

  const columns = [
    { key: 'created_at', label: 'Created At', render: (item) => formatDateTime(item.created_at) },
    {
      key: 'service_number', label: 'Service Number', render: (item) => (
        <button
          className="text-blue-600 hover:text-blue-800 font-medium underline"
          onClick={() => openService(item)}
        >
          {item.service_number}
        </button>
      )
    },
    { key: 'case_number', label: 'Case Number' },
    { key: 'service_type', label: 'Service Type' },
    { key: 'service_subtype', label: 'Service Sub-Type' },
    {
      key: 'status', label: 'Status', render: (item) => (
        <div className="flex flex-col gap-1">
          <span className={`px-2 py-1 inline-block text-xs leading-5 font-semibold rounded-md w-full text-center capitalize ${item.status === 'service_complete' || item.status === 'service_resolved' ? 'bg-green-100 text-green-800' : item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>{item.status ? item.status.replace(/_/g, ' ').toUpperCase() : 'N/A'}</span>
          {item.completion_otp_escalated ? (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-md bg-red-100 text-red-800 text-center">
              OTP Escalated
            </span>
          ) : null}
        </div>
      )
    },
    { key: 'customer_name', label: 'Customer Name' },
    { key: 'vendor_name', label: 'Vendor Name' },
    { key: 'driver_name', label: 'Driver Name' },
    {
      key: 'actions',
      label: '',
      render: (item) => (
        <button
          className="px-4 py-2 bg-scarlet-600 text-white rounded hover:bg-blue-700"
          onClick={() => item.onViewService(item)}
        >
          {item.status === 'awaiting_customer_payment' ? 'Assign Vendor' : 'Track Vendor'}
        </button>
      ),
    },
  ];

  const fetchServices = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAllServices();
      // Map data for table and filter out completed services
      const items = (res?.items || [])
        .filter(s => s.status !== 'service_complete') // Filter out completed services
        .map(s => ({
          id: s.id,
          case_id: s.case_id, // Add case_id for navigation
          service_number: s.service_number,
          case_number: s.case?.case_number || 'N/A',
          status: s.status,
          completion_otp_escalated: !!(s.meta_data?.completion_otp_escalated),
          service_type: s.service_type || 'N/A',
          service_subtype: s.service_subtype || 'N/A',
          customer_name: s.case?.customer?.name || 'N/A',
          vendor_name: s.vendor_location?.dealer?.dealer_name || 'N/A',
          driver_name: s.driver?.name || 'N/A',
          created_at: s.created_at,
          onViewService: () =>
            openService(s, {
              track:
                s.status !== 'awaiting_customer_payment' &&
                serviceStatusAllowsVendorTracking(s.status),
            }),
        }));
      setServices(items);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message || 'Failed to fetch services');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleRefresh = () => {
    fetchServices();
  };

  const escalatedCount = services.filter((s) => s.completion_otp_escalated).length;
  const visibleServices = escalatedOnly ? services.filter((s) => s.completion_otp_escalated) : services;

  return (
    <>
      <div className='mt-0 mb-4'>
        <div className="flex justify-between items-center px-3">
          <h1 className="text-2xl font-semibold">Services</h1>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setEscalatedOnly((v) => !v)}
              aria-pressed={escalatedOnly}
              className={`px-4 py-2 text-sm font-medium rounded border transition-colors ${
                escalatedOnly
                  ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                  : 'bg-white text-red-700 border-red-300 hover:bg-red-50'
              }`}
            >
              OTP escalated only ({escalatedCount})
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black border rounded hover:bg-gray-50 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>
      <div className="p-6 bg-white mrgall rounded-lg border bxshadowhd">

        <SortableTable
          columns={columns}
          data={visibleServices}
          emptyMessage={loading ? 'Loading...' : escalatedOnly ? 'No OTP-escalated services' : 'No services found'}
          containerClassName="w-full overflow-x-auto"

          initialSortKey="created_at"
          initialSortOrder="desc"
        />
      </div>
    </>
  );
};

export default Services; 